<script setup lang="ts">
import ButtonOutlined from '@/shared/components/button/ButtonOutlined.vue';
import ThumbsUpIcon from '../assets/icon/thumbs-up.svg';
import ThumbsDownIcon from '../assets/icon/thumbs-down.svg';

const props = withDefaults(
  defineProps<{
    min: number;
    max: number;
    modelValue: number | undefined;
    disabled?: boolean;
  }>(),
  {
    min: 1,
    max: 10,
    modelValue: 0,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: number): void;
}>();

const updateValue = (value: number) => {
  if (props.disabled) {
    return; // Do not allow to update the value if the component is disabled
  }
  emit('update:modelValue', value);
};
</script>

<template>
  <div class="component-container">
    <div class="error-message">
      <slot name="error"></slot>
    </div>
    <div
      class="rating-container"
      role="slider"
      :aria-valuemin="min"
      :aria-valuemax="max"
      :aria-valuenow="modelValue ?? 0"
    >
      <div class="rating-button-container" v-for="i in max" :key="i">
        <ButtonOutlined
          :disabled="disabled"
          class="rating-button"
          :class="{ 'rating-button_selected': modelValue === i }"
          @click="updateValue(i)"
          :data-tn="`rating-button-${i}`"
          >{{ i }}</ButtonOutlined
        >
        <ThumbsUpIcon v-if="max === i" />
        <ThumbsDownIcon v-else-if="min === i" />
        <div v-else class="thumb-placeholder"></div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.component-container {
  width: 100%;
}

.rating-container {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
}

.rating-button-container {
  display: flex;
  min-width: 10%;
  align-items: center;
  flex-direction: column;
  gap: 10px;

  .rating-button {
    width: 100%;
    border-radius: 0;
    min-height: 64px;
    font-size: x-large;
    font-weight: @font-weight-regular;

    &[disabled] {
      cursor: default;
      color: @color-secondary;
    }
    &[disabled].rating-button_selected {
      background-color: @color-mid-green;
      color: @color-primary;
    }

    &_selected {
      background-color: @color-mid-tone;
    }

    &:focus {
      border-width: 1.5px;
    }
  }

  &:not(:first-child) .rating-button {
    border-left-width: 0;
  }

  &:first-child .rating-button {
    border-top-left-radius: @border-radius-medium;
    border-bottom-left-radius: @border-radius-medium;
  }

  &:last-child .rating-button {
    border-top-right-radius: @border-radius-medium;
    border-bottom-right-radius: @border-radius-medium;
  }
}

.rating-value {
  min-width: 24px;
  text-align: center;
}

.thumb-placeholder {
  height: 18px;
}

.error-message {
  color: @color-error;
  font-size: 12px;
  margin-bottom: 5px;
}
</style>